/**
 * Conversational voice for Ask Copilot (iter336).
 *
 * Mounted by Copilot.jsx while a call is up. The backend mints a short-lived OpenAI
 * Realtime secret (with this community's brain + read-only tools baked in) and the browser
 * talks straight to OpenAI over WebRTC. Tool calls come back to our API, so every permission
 * the dashboard enforces still applies. Every finished turn is handed up via `onTurn`, so the
 * spoken exchange lands in the SAME chat thread the manager was typing in.
 *
 * Ten-minute hard cap with a one-minute warning — a call burns real voice minutes.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Mic, PhoneOff, RotateCcw, Search, AlertTriangle } from "lucide-react";
import { V } from "@/lib/vocab";

const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const CopilotVoiceBar = ({ tenantId, tenantName, history, onTurn, onProposal, onClose }) => {
  const [status, setStatus] = useState("connecting");   // connecting | live | error
  const [error, setError] = useState("");
  const [caption, setCaption] = useState("");
  const [toolLabel, setToolLabel] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [limit, setLimit] = useState(600);
  const [attempt, setAttempt] = useState(0);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micRef = useRef(null);
  const audioRef = useRef(null);
  const bufRef = useRef("");
  const warnedRef = useRef(false);
  const overRef = useRef(false);

  const teardown = () => {
    try { dcRef.current?.close(); } catch { /* already closed */ }
    try {
      pcRef.current?.getSenders?.().forEach((s) => s.track?.stop());
      pcRef.current?.close();
    } catch { /* already closed */ }
    try { micRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* already stopped */ }
    if (audioRef.current) audioRef.current.srcObject = null;
    dcRef.current = null; pcRef.current = null; micRef.current = null;
  };

  const finish = (note) => {
    if (overRef.current) return;
    overRef.current = true;
    if (bufRef.current.trim()) onTurn({ role: "assistant", content: bufRef.current.trim() });
    teardown();
    if (note) toast.info(note);
    onClose();
  };

  // A failure mid-call has to LOOK like one — the bar used to stay green over a dead session.
  const fail = (message) => {
    setStatus("error");
    setError(message);
    setCaption("");
    teardown();
  };

  const retry = () => {
    bufRef.current = "";
    warnedRef.current = false;
    setError(""); setCaption(""); setToolLabel(""); setSeconds(0);
    setStatus("connecting");
    setAttempt((a) => a + 1);
  };

  const runTool = async (name, callId, argsRaw) => {
    let args = {};
    try { args = JSON.parse(argsRaw || "{}"); } catch { /* no args */ }
    let payload = { ok: false, note: "That lookup failed." };
    try {
      const r = await api.post(`/tenants/${tenantId}/copilot/voice/tool`, { name, arguments: args });
      payload = r.data?.result ?? payload;
      setToolLabel(r.data?.label || "");
      // A spoken action proposal lands as a card in the chat thread — the call itself can
      // never fire it, so the manager's finger is still the trigger.
      if (r.data?.proposal) onProposal?.(r.data.proposal);
    } catch {
      setToolLabel("");
    }
    const dc = dcRef.current;
    if (dc && dc.readyState === "open") {
      dc.send(JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: callId, output: JSON.stringify(payload) },
      }));
      dc.send(JSON.stringify({ type: "response.create" }));
    }
    setTimeout(() => setToolLabel(""), 2500);
  };

  const handleEvent = (ev) => {
    const t = ev.type || "";
    if (t === "response.output_audio_transcript.delta" || t === "response.audio_transcript.delta") {
      bufRef.current += ev.delta || "";
      setCaption(bufRef.current.slice(-220));
    } else if (t === "response.output_audio_transcript.done" || t === "response.audio_transcript.done") {
      const text = (ev.transcript || bufRef.current).trim();
      bufRef.current = "";
      setCaption("");
      if (text) onTurn({ role: "assistant", content: text });
    } else if (t === "conversation.item.input_audio_transcription.completed") {
      const said = (ev.transcript || "").trim();
      if (said) onTurn({ role: "user", content: said });
    } else if (t === "response.function_call_arguments.done") {
      runTool(ev.name, ev.call_id, ev.arguments);
    } else if (t === "error") {
      fail(ev.error?.message || "The voice service dropped the call. Try again.");
    }
  };

  // Connect once per attempt. The guard object survives StrictMode's double-invoke in dev:
  // the first run aborts before it ever opens a peer connection, so only one session goes live.
  useEffect(() => {
    const run = { aborted: false };
    (async () => {
      try {
        const { data } = await api.post(`/tenants/${tenantId}/copilot/voice/session`, {
          history: (history || []).map((m) => ({ role: m.role, content: m.content })),
        });
        if (run.aborted) return;
        setLimit(data.max_seconds || 600);

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        if (run.aborted) { stream.getTracks().forEach((t) => t.stop()); return; }
        micRef.current = stream;

        const pc = new RTCPeerConnection();
        pcRef.current = pc;
        pc.ontrack = (e) => { if (audioRef.current) audioRef.current.srcObject = e.streams[0]; };
        stream.getTracks().forEach((tr) => pc.addTrack(tr, stream));

        const dc = pc.createDataChannel("oai-events");
        dcRef.current = dc;
        dc.onopen = () => {
          setStatus("live");
          dc.send(JSON.stringify({ type: "response.create" }));
        };
        dc.onmessage = (e) => { try { handleEvent(JSON.parse(e.data)); } catch { /* ignore */ } };

        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const res = await fetch(
          `https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(data.model)}`,
          {
            method: "POST",
            body: offer.sdp,
            headers: {
              Authorization: `Bearer ${data.client_secret}`,
              "Content-Type": "application/sdp",
            },
          });
        if (run.aborted) return;
        if (!res.ok) throw new Error("Couldn't open the audio channel.");
        await pc.setRemoteDescription({ type: "answer", sdp: await res.text() });
      } catch (e) {
        if (run.aborted) return;
        fail(e?.name === "NotAllowedError"
          ? "Microphone access was blocked — allow it in your browser and try again."
          : (e?.response?.data?.detail || e?.message || "Couldn't start the conversation."));
      }
    })();
    return () => { run.aborted = true; teardown(); };
  }, [tenantId, attempt]);

  // Elapsed clock + the cost guard.
  useEffect(() => {
    if (status !== "live") return;
    const id = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  useEffect(() => {
    if (status !== "live") return;
    if (!warnedRef.current && seconds >= limit - 60) {
      warnedRef.current = true;
      toast.warning("About a minute left on this call — it ends at ten minutes.");
    }
    if (seconds >= limit) finish("Call ended at the ten-minute limit. Start another any time.");
  }, [seconds, status, limit]);

  const live = status === "live";
  const errored = status === "error";

  return (
    <div className="max-w-3xl mx-auto mb-2" data-testid="copilot-voice-bar">
      <audio ref={audioRef} autoPlay className="hidden" />
      <div className={`rounded-2xl border px-3.5 py-2.5 flex items-center gap-3 ${
        errored ? "border-amber-300 bg-amber-50" : "border-emerald-300 bg-emerald-50/60"
      }`}>
        <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${
          live ? "bg-emerald-500 text-white animate-pulse" : "bg-white text-ink/50 border border-sand-200"
        }`}>
          {status === "connecting"
            ? <Loader2 className="h-4 w-4 animate-spin" />
            : errored
              ? <AlertTriangle className="h-4 w-4 text-amber-600" />
              : <Mic className="h-4 w-4" strokeWidth={2.25} />}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-ink flex items-center gap-2" data-testid="copilot-voice-status">
            {status === "connecting" && "Connecting your mic…"}
            {live && (toolLabel
              ? <><Search className="h-3.5 w-3.5 text-emerald-700" /> {toolLabel}</>
              : `Talking with Copilot about ${tenantName || `this ${V.lc("org")}`}`)}
            {errored && "The conversation stopped"}
            {live && (
              <span className="text-[11px] font-normal text-ink/50" data-testid="copilot-voice-timer">
                {mmss(seconds)} / {mmss(limit)}
              </span>
            )}
          </div>
          <div className="text-[11px] text-ink/60 truncate" data-testid="copilot-voice-caption">
            {errored
              ? error
              : caption || (live
                ? "Speak whenever you like — interrupt any time. Everything said lands in this chat."
                : "Allow the microphone when your browser asks.")}
          </div>
        </div>

        {errored && (
          <button
            onClick={retry}
            className="shrink-0 rounded-full border border-ink/15 bg-white text-ink text-xs font-semibold px-3 py-2 flex items-center gap-1.5 hover:bg-sand-50 transition-colors"
            data-testid="copilot-voice-retry"
          >
            <RotateCcw className="h-3.5 w-3.5" strokeWidth={2.25} /> Try again
          </button>
        )}
        <button
          onClick={() => finish("")}
          className="shrink-0 rounded-full bg-ink text-white text-xs font-semibold px-3.5 py-2 flex items-center gap-1.5 hover:bg-ink-700 transition-colors"
          data-testid="copilot-voice-end"
        >
          <PhoneOff className="h-3.5 w-3.5" strokeWidth={2.25} /> {errored ? "Close" : "End"}
        </button>
      </div>
    </div>
  );
};
